import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";

export interface BehaviorSnapshotScheduleSettings {
  isEnabled: boolean;
  timezone: string;
  runTimeLocal: string;
  lastRunAt: string | null;
  lastRunSnapshotDate: string | null;
  lastError: string | null;
  updatedBy: string | null;
}

export interface BehaviorManualRunResult {
  ok: boolean;
  snapshotDate?: string;
  rowsWritten?: number;
  source?: string;
  noOp?: boolean;
  message?: string;
  error?: string;
}

export interface BehaviorHistoricalRebuildResult {
  ok: boolean;
  datesProcessed: number;
  rowsWritten: number;
  errors: Array<{ date: string; error: string }>;
  message: string;
}

export function useBehaviorScheduleSettings() {
  return useQuery<BehaviorSnapshotScheduleSettings>({
    queryKey: ["/api/admin/snapshots/behavior/settings"],
  });
}

export function useUpdateBehaviorScheduleSettings() {
  return useMutation({
    mutationFn: async (data: { isEnabled?: boolean; runTimeLocal?: string }) => {
      return apiRequest("POST", "/api/admin/snapshots/behavior/settings", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/snapshots/behavior/settings"] });
    },
  });
}

export function useBehaviorManualSnapshotRun() {
  return useMutation<BehaviorManualRunResult, Error, { snapshotDate?: string; force?: boolean }>({
    mutationFn: async ({ snapshotDate, force = false }) => {
      const params = new URLSearchParams();
      if (snapshotDate) params.set("snapshot_date", snapshotDate);
      if (force) params.set("force", "true");
      const url = `/api/admin/snapshots/behavior/run${params.toString() ? "?" + params.toString() : ""}`;
      return apiRequest("POST", url, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/snapshots/behavior/settings"] });
    },
  });
}

export function useBehaviorHistoricalRebuild() {
  return useMutation<BehaviorHistoricalRebuildResult, Error, { days: number }>({
    mutationFn: async ({ days }) => {
      return apiRequest("POST", "/api/admin/snapshots/behavior/rebuild", { days });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/snapshots/behavior/settings"] });
    },
  });
}
